/**
 * UnifiedUploadProgress - Batch upload progress display
 * 
 * Shows:
 * - Overall batch progress summary
 * - Individual file status cards
 * - Transaction counts
 * - Processing stages
 */

import React from 'react';
import { 
  FileText, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  FileSpreadsheet,
  Sparkles,
  Cloud,
  BarChart3,
  Clock
} from 'lucide-react';
import type { FileUploadState } from '../../hooks/useUnifiedUpload';

interface UnifiedUploadProgressProps {
  files: FileUploadState[];
  summary?: {
    total: number;
    completed: number;
    processing: number;
    failed: number;
    successRate: string;
  };
  totalTransactions?: number;
  onRetry?: (index: number) => void;
  showDetails?: boolean;
  compact?: boolean;
}

const STAGE_LABELS: Record<string, string> = {
  'downloading': 'Downloading from S3...',
  'fingerprinting': 'Analyzing document...',
  'messiness_check': 'Checking quality...',
  'pdfplumber': 'Extracting with PDFPlumber...',
  'pdfplumber_cached': 'Using cached rules...',
  'claude_ocr': 'AI Processing (Claude)...',
  'textract': 'AWS Textract OCR...',
  'csv_parsing': 'Parsing CSV...',
  'excel_parsing': 'Parsing Excel...',
  'complete': 'Complete!',
  'error': 'Failed',
};

function getStatusIcon(status: string, stage?: string | null) {
  switch (status) {
    case 'parsed':
      return <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />;
    case 'failed':
      return <XCircle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />;
    case 'processing':
      if (stage?.includes('claude')) return <Sparkles className="w-5 h-5 animate-pulse" style={{ color: 'var(--color-needs-human-text)' }} />;
      if (stage?.includes('textract')) return <Cloud className="w-5 h-5 animate-pulse" style={{ color: 'var(--color-needs-human-text)' }} />;
      return <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />;
    case 'uploading':
      return <Cloud className="w-5 h-5 animate-pulse" style={{ color: 'var(--color-needs-human-text)' }} />;
    default:
      return <FileText className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />;
  }
}

export default function UnifiedUploadProgress({ 
  files, 
  summary,
  totalTransactions = 0,
  onRetry,
  showDetails = true,
  compact = false,
}: UnifiedUploadProgressProps) {
  if (files.length === 0) return null;

  const overallProgress = files.length > 0
    ? Math.round(files.reduce((sum, f) => sum + f.progress, 0) / files.length)
    : 0;

  return (
    <div className="space-y-4">
      {/* Overall Progress Header */}
      <div className="border rounded-lg p-4" style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Batch Upload Progress
            </span>
          </div>
          {summary && (
            <div className="flex items-center gap-4 text-sm">
              <span className="font-medium" style={{ color: 'var(--color-verified)' }}>
                ✓ {summary.completed}
              </span>
              <span className="inline-flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                <Clock size={13} /> {summary.processing}
              </span>
              {summary.failed > 0 && (
                <span style={{ color: 'var(--color-needs-human-text)' }}>
                  ✗ {summary.failed}
                </span>
              )}
              <span style={{ color: 'var(--color-text-muted)' }}>
                / {summary.total} files
              </span>
            </div>
          )}
        </div>

        {/* Overall Progress Bar */}
        <div className="h-3 rounded-full overflow-hidden mb-2" style={{ background: 'var(--color-needs-human-tint)' }}>
          <div
            className="h-full transition-all duration-500 ease-out"
            style={{ width: `${overallProgress}%`, background: 'var(--color-brand)' }}
          />
        </div>

        <div className="flex justify-between text-sm">
          <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>{overallProgress}% complete</span>
          {totalTransactions > 0 && (
            <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
              {totalTransactions.toLocaleString()} transactions extracted
            </span>
          )}
        </div>
      </div>

      {/* Individual File Progress */}
      {showDetails && (
        <div className={`space-y-2 ${compact ? 'max-h-64 overflow-y-auto' : ''}`}>
          {files.map((file, index) => (
            <div
              key={file.file.name + index}
              className="flex items-center gap-3 p-3 rounded-lg border transition-all"
              style={
                file.status === 'parsed'
                  ? { background: 'transparent', borderColor: 'var(--color-verified)' }
                  : file.status === 'failed'
                  ? { background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
                  : { background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }
              }
            >
              {/* Status Icon */}
              {getStatusIcon(file.status)}

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate text-sm" style={{ color: 'var(--color-text-primary)' }}>
                    {file.file.name}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>
                    ({(file.file.size / 1024).toFixed(0)} KB)
                  </span>
                </div>

                {/* Status Text */}
                <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {file.status === 'parsed' && (
                    <span style={{ color: 'var(--color-verified)' }}>
                      ✓ {file.transactionsCount || file.result?.transactions?.length || 0} transactions
                      {file.retryCount && file.retryCount > 0 && (
                        <span className="ml-1" style={{ color: 'var(--color-text-dim)' }}>(after retry)</span>
                      )}
                    </span>
                  )}
                  {file.status === 'failed' && (
                    <span style={{ color: 'var(--color-needs-human-text)' }}>
                      {file.error || 'Processing failed'}
                      {file.retryCount && file.retryCount > 0 && (
                        <span className="ml-1" style={{ color: 'var(--color-text-muted)' }}>(retried {file.retryCount}x)</span>
                      )}
                    </span>
                  )}
                  {file.status === 'processing' && (
                    <span style={{ color: 'var(--color-needs-human-text)' }}>
                      {file.retryCount && file.retryCount > 0 ? 'Retrying... ' : ''}{file.progress}%
                    </span>
                  )}
                  {file.status === 'uploading' && (
                    <span style={{ color: 'var(--color-needs-human-text)' }}>Uploading to S3...</span>
                  )}
                </div>
              </div>

              {/* Progress or Actions */}
              <div className="flex items-center gap-2">
                {file.status === 'processing' && (
                  <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <div
                      className="h-full transition-all duration-300"
                      style={{ width: `${file.progress}%`, background: 'var(--color-brand)' }}
                    />
                  </div>
                )}
                {file.status === 'failed' && onRetry && (
                  <button
                    onClick={() => onRetry(index)}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded border"
                    style={{ color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' }}
                    title="Retry processing this file"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Retry
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {summary && summary.processing === 0 && (
        <div
          className="p-3 rounded-lg"
          style={
            summary.failed === 0
              ? { background: 'transparent', color: 'var(--color-verified)' }
              : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
          }
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {summary.failed === 0 ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertTriangle className="w-5 h-5" />
              )}
              <span className="font-medium">
                {summary.failed === 0 
                  ? `All ${summary.total} files processed successfully!`
                  : `${summary.completed} of ${summary.total} files processed (${summary.failed} failed)`
                }
              </span>
            </div>
            {totalTransactions > 0 && (
              <span className="font-semibold">
                {totalTransactions.toLocaleString()} total transactions
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
